/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

export type ContractorFieldKey =
  | "ContractorName"
  | "Acronym"
  | "OrganizationType"
  | "PhoneNumber"
  | "EmailAddress"
  | "PostalAddress"
  | "Website"
  | "PhysicalAddress"
  | "City"
  | "Country"
  | "BusinessOwner"
  | "OldNid"
  | "UNGMNumber"
  | "ContactPerson";

export type FieldMapping = Partial<Record<ContractorFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const CONTRACTOR_FIELDS: Array<{
  key: ContractorFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "ContractorName", label: "Contractor Name", required: true },
  { key: "Acronym", label: "Acronym" },
  { key: "OrganizationType", label: "Organization Type" },
  { key: "PhoneNumber", label: "Phone Number" },
  { key: "EmailAddress", label: "Email Address" },
  { key: "PostalAddress", label: "Postal Address" },
  { key: "Website", label: "Website" },
  { key: "PhysicalAddress", label: "Physical Address" },
  { key: "City", label: "City" },
  { key: "Country", label: "Country" },
  { key: "BusinessOwner", label: "Business Owner" },
  { key: "OldNid", label: "Old NID" },
  { key: "UNGMNumber", label: "UNGM Number" },
  { key: "ContactPerson", label: "Contact Person" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Compares normalised strings and returns a 0–2 similarity score. */
function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return (
      Math.min(a.length, b.length) / Math.max(a.length, b.length)
    );
  }
  return 0;
}

/**
 * Given a list of CSV headers, returns the best-guess FieldMapping by
 * normalising both the contractor field key and each header and picking
 * the closest match. Already-claimed headers are not reused.
 */
export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of CONTRACTOR_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

/** Converts a raw CSV row + FieldMapping into the shape expected by the API. */
export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: ContractorFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    contractorName: get("ContractorName") ?? "",
    acronym: get("Acronym"),
    organizationType: get("OrganizationType"),
    phoneNumber: get("PhoneNumber"),
    emailAddress: get("EmailAddress"),
    postalAddress: get("PostalAddress"),
    website: get("Website"),
    physicalAddress: get("PhysicalAddress"),
    city: get("City"),
    country: get("Country"),
    businessOwner: get("BusinessOwner"),
    oldNid: get("OldNid"),
    ungmNumber: get("UNGMNumber"),
    contactPerson: get("ContactPerson"),
  };
}

/** Builds a client-side CSV blob from failed rows using the original headers. */
export function buildFailedCsvBlob(
  failedRows: Record<string, string>[],
  headers: string[]
): Blob {
  const escape = (v: string) =>
    `"${(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...failedRows.map((row) => headers.map((h) => escape(row[h] ?? "")).join(",")),
  ];
  return new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
}
